import type {
  AppearancePalette,
  AppearancePreference,
  WebsiteAppearance,
} from "@/cms/appearance";
import type { DerivedSurface } from "@/types/look-system";

export function appearanceCookieName(applicationId?: string) {
  return `reweb-appearance-${applicationId ?? "website"}`;
}

export function resolveAppearance(
  settings: WebsiteAppearance,
  cookie?: string,
): AppearancePreference {
  let saved: Partial<AppearancePreference> = {};
  try {
    saved = JSON.parse(decodeURIComponent(cookie ?? "{}")) ?? {};
  } catch {
    /* Invalid preferences use the website default. */
  }
  const scheme = settings.defaultScheme ?? "dark";
  const shape = settings.defaultShape ?? "rounded";
  return {
    scheme:
      settings.allowSchemeSwitch &&
      (saved.scheme === "light" || saved.scheme === "dark") &&
      settings.palettes?.[saved.scheme]
        ? saved.scheme
        : scheme,
    shape,
  };
}

function appearanceVars(
  palette: AppearancePalette,
  scheme: string,
): Record<string, string> {
  return {
    background: palette.background,
    foreground: palette.text,
    "accent-transparent":
      "color-mix(in srgb, var(--re-accent) 18%, transparent)",
    "re-background": palette.background,
    "re-text": palette.text,
    "re-surface": palette.surface,
    "re-muted-surface": scheme === "dark" ? "#ffffff14" : palette.border,
    "re-hover-fill":
      "color-mix(in srgb, var(--re-muted-surface) 30%, transparent)",
    "re-on-image": "#ffffff",
    "re-media-background": "#11100d",
    "re-slider-range": palette.accent,
    "re-slider-thumb": palette.text,
    "re-hero-text-shadow":
      scheme === "dark" ? "0 8px 40px #0006" : "0 2px 24px #fff4",
    "re-error": scheme === "dark" ? "#ffb4ab" : "#b42318",
    "re-error-background": "#b42318",
    "re-error-text": "#ffffff",
    "page-bg-color": palette.background,
    "base-bg-color": palette.background,
    "base-text-color": palette.text,
    "page-text-color": palette.text,
    "page-text-heading-color": palette.text,
    "page-heading-color": palette.text,
    "header-text-color": palette.text,
    "header-visible-text-color": palette.text,
    "header-active-text-color": palette.accent,
    "header-bg-color": palette.background,
    "menu-bg-color": palette.background,
    "menu-text-color": palette.text,
    "card-bg-color": palette.surface,
    "card-text-color": palette.text,
    "classic-card-button-bg-color": palette.background,
    "classic-card-button-text-color": palette.text,
    "classic-card-button-hover-bg-color": palette.accent,
    "classic-card-button-hover-text-color": palette.accentText,
    "footer-bg-color": palette.background,
    "footer-text-color": palette.text,
    "search-bg-color": palette.background,
    "search-text-color": palette.text,
    "search-close-button-text-color": palette.text,
    "input-bg-color": palette.surface,
    "input-text-color": palette.text,
    "label-bg-color": palette.surface,
    "label-text-color": palette.text,
    "testimony-bg-color": palette.surface,
    "testimony-image-bg-color": palette.surface,
    "testimony-text-color": palette.text,
    "testimony-quote-color": palette.accent,
    "slider-active-bg-color": palette.accent,
    "slider-bg-color": palette.border,
    "slider-border-color": palette.border,
    "slider-thumb-color": palette.text,
    "hightlight-bg-color": palette.surface,
    "hightlight-text-color": palette.text,
    "tabs-bg-color": palette.background,
    "tabs-text-color": palette.muted,
    "tabs-active-bg-color": palette.surface,
    "tabs-active-text-color": palette.text,
    "swiper-pagination-color": palette.text,
    "swiper-theme-color": palette.accent,
    "swiper-pagination-bullet-inactive-color": palette.text,
    "select-option-bg-color": palette.surface,
    "select-option-text-color": palette.text,
    "checkbox-bg-color": palette.text,
    "checkbox-text-color": palette.background,
    "calendar-bg-color": palette.text,
    "calendar-hover-bg-color":
      "color-mix(in srgb, var(--re-accent) 18%, transparent)",
    "button-bg-color": palette.text,
    "button-text-color": palette.background,
    "button-hover-bg-color": palette.accent,
    "button-hover-text-color": palette.accentText,
    "button-border-color": palette.border,
    "button-hover-border-color": palette.accent,
    "button-active-bg-color": palette.accent,
    "button-active-text-color": palette.accentText,
    "button-active-border-color": palette.accent,
    "error-button-bg-color": "var(--re-error-background)",
    "error-button-text-color": "var(--re-error-text)",
    "error-button-border-color": "var(--re-error-background)",
    "error-button-hover-bg-color": palette.surface,
    "error-button-hover-text-color": "var(--re-error)",
    "error-button-hover-border-color": "var(--re-error)",
    "error-button-active-bg-color": "var(--re-error-background)",
    "error-button-active-text-color": "var(--re-error-text)",
    "error-button-active-border-color": "var(--re-error-background)",
    "filter-button-bg-color": "#ffffff00",
    "filter-button-text-color": palette.text,
    "filter-button-border-color": "#ffffff00",
    "filter-button-hover-bg-color": palette.surface,
    "filter-button-hover-text-color": palette.text,
    "filter-button-hover-border-color": palette.border,
    "filter-button-active-bg-color": palette.text,
    "filter-button-active-text-color": palette.background,
    "filter-button-active-border-color": palette.text,
    "re-muted": palette.muted,
    "re-border": palette.border,
    "re-accent": palette.accent,
    "re-accent-text": palette.accentText,
    "sort-text-color": palette.text,
    "sort-bg-color": palette.surface,
    "sort-hover-text-color": palette.accentText,
    "sort-hover-bg-color": palette.accent,
    "dropdown-text-color": palette.text,
    "dropdown-bg-color": palette.surface,
    "dropdown-hover-text-color": palette.accentText,
    "dropdown-hover-bg-color": palette.accent,
    "popup-bg-color": palette.surface,
    "popup-text-color": palette.text,
    "search-input-bg-color": palette.surface,
    "search-input-text-color": palette.text,
    "search-button-bg-color": palette.text,
    "search-button-text-color": palette.background,
    "search-button-hover-bg-color": palette.accent,
    "search-button-hover-text-color": palette.accentText,
    "form-button-bg-color": palette.text,
    "form-button-text-color": palette.background,
    "form-button-hover-bg-color": palette.accent,
    "form-button-hover-text-color": palette.accentText,
    "dialog-primary-color": palette.text,
    "dialog-error-color": "var(--re-error)",
    "dialog-bg-color": palette.surface,
    "dialog-text-color": palette.text,
    "dialog-muted-text-color": palette.muted,
    "dialog-icon-text-color": palette.background,
    "dialog-backdrop-color": "#00000080",
    "partner-bg-color": palette.background,
    "partner-text-color": palette.muted,
    "map-pin-color": palette.accent,
    "gallery-bg-color": palette.background,
    "gallery-text-color": palette.text,
    "gallery-placeholder-bg-color": palette.surface,
    "overlay-bg-color": palette.surface,
    "overlay-text-color": palette.text,
    "casedetails-submenu-bg-color": palette.surface,
    "casedetails-submenu-text-color": palette.text,
    "casedetails-submenu-hover-bg-color": palette.accent,
    "casedetails-submenu-hover-text-color": palette.accentText,
    "casedetails-submenu-active-bg-color": palette.text,
    "casedetails-submenu-active-text-color": palette.background,
  };
}

export function appearanceCss(settings?: WebsiteAppearance) {
  if (!settings) return "";
  return Object.entries(settings.palettes ?? {})
    .map(([scheme, palette]) => {
      if (!palette) return "";
      return `html[data-re-scheme="${scheme}"] { color-scheme: ${scheme}; ${Object.entries(
        appearanceVars(palette, scheme),
      )
        .map(([key, value]) => `--${key}: ${value};`)
        .join("\n")} }`;
    })
    .join("\n");
}

/**
 * The legacy variables and `--re-*` roles for one look-system background, through the same mapping as
 * `appearanceCss`, so classic components read the derived colours wherever they read a theme palette.
 */
export function legacyVarsFromSurface(
  surface: DerivedSurface,
): Record<string, string> {
  const { btn } = surface;
  const fill = btn.bg === "transparent" ? btn.hover : btn;
  const button = (prefix: string) => ({
    [`${prefix}-bg-color`]: btn.bg,
    [`${prefix}-text-color`]: btn.fg,
    [`${prefix}-hover-bg-color`]: btn.hover.bg,
    [`${prefix}-hover-text-color`]: btn.hover.fg,
  });
  return {
    ...appearanceVars(
      {
        background: surface.bg,
        text: surface.text,
        muted: surface.muted,
        surface: surface.card,
        border: surface.border,
        accent: fill.bg,
        accentText: fill.fg,
      },
      surface.dark === true ? "dark" : "light",
    ),
    "page-text-heading-color": surface.heading,
    "page-heading-color": surface.heading,
    "swiper-theme-color": surface.link,
    "re-error": surface.error,
    ...button("button"),
    "button-border-color": btn.border,
    "button-hover-border-color": btn.hover.bg,
    "button-active-bg-color": btn.active.bg,
    "button-active-text-color": btn.active.fg,
    "button-active-border-color": btn.active.bg,
    ...button("form-button"),
    ...button("search-button"),
  };
}
